import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import debounce from "lodash/debounce";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  EVENT_ID,
  PASSWORD_TYPE,
  ROLE,
  SHARE_KEY_PREFIX,
  Space,
  SpaceVisibility,
  SUBSCRIPTION_TIER,
} from "@highnote/server/src/core/entities";
import {
  getEntityShareKeys,
  getFullRoles,
  isSpacePrivateInboxEnabled,
} from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import { CopyButton } from "App/common/CopyButton";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import { List, ListItemButton } from "App/common/List";
import { TextInput } from "App/common/TextInput";
import { ReactComponent as ChatSVG } from "App/common/icons/chat.svg";
import { ReactComponent as ChatFilledSVG } from "App/common/icons/chat-filled.svg";
import { ReactComponent as DownloadSVG } from "App/common/icons/download.svg";
import { ReactComponent as EyeSVG } from "App/common/icons/eye.svg";
import { ReactComponent as LinkSVG } from "App/common/icons/link-hollow.svg";
import { ReactComponent as UploadSVG } from "App/common/icons/upload.svg";
import { ReactComponent as UserSVG } from "App/common/icons/user-hollow.svg";
import { useToast } from "App/common/useToast";
import { getAuthId } from "App/components/Auth";
import { EntityPasswordEditor } from "App/components/EntityPasswordEditor";
import { PermissionTooltip } from "App/components/PermissionTooltip";
import { BUTTON_THEME } from "App/core/Button";
import {
  CONTROLLED_SHARE_FEATURES,
  ControlledFeatureTooltipLabel,
} from "App/core/ControlledFeatureCallout";
import { Switch } from "App/core/Switch";
import { useSegmentContext } from "App/modules/useSegment";
import { useUrlContext } from "App/routes/Main/useUrlContext";
import { useShareDialogContext } from "../ShareDialogContext";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { useMyMemberships } from "App/components/Auth/MyMembershipsContext";
import { useSpaceAccessMembers } from "App/components/useEntities/useSpaceAccessMembers";

export const useEntityShareKeyInUrl = (entity: Space) => {
  const { toasted } = useToast();
  const history = useHistory();
  const { resolveMember } = useMyMemberships();
  const { membersV2Enabled } = useMembershipFlags();

  useEffect(() => {
    if (!entity) return;
    const query = new URLSearchParams(location.search);
    const shareKeyInUrl = query.get("shareKey");
    /*
     * Warning: this value stays on the legacy `rolesV2` map while the
     * migration still writes the legacy fields. The Firestore `list` rule
     * for member documents grants `type == 'share'` reads to managers only.
     * So there is no member-document source for this value for exactly the
     * people who need it: a non-manager reader whose URL must keep its
     * `?shareKey=`. The alternative is a rules change that lets any reader
     * list share documents, and a client flag cannot roll back a rules
     * change that grants more access. A later phase of the migration
     * replaces this read with the `getSpaceEntryGrant` callable.
     */
    const shareKeyInEntity = getEntityShareKeys(entity)[0];
    if (!shareKeyInEntity) {
      const authId = getAuthId();
      /*
       * This read does move to the new model, and it moved with the
       * migration's permissions group rather than its share-link group. It
       * is a question about the caller's own grant, and the member-document
       * engine answers that for exactly the identity that asked. So the
       * manager-only problem above does not apply here. A file can carry
       * two flags; a behavior must carry exactly one.
       *
       * Note: this is a roles test, not a presence test. It is the opposite
       * of `isPublicView`, where a blocked member is present. A blocked
       * member has `effectiveRoles: []` and is not an entity member here.
       * Do not unify the two.
       */
      const member = membersV2Enabled
        ? resolveMember(entity.id, authId)
        : undefined;
      const isEntityMember =
        member === undefined
          ? (getFullRoles(entity)[authId] || []).length > 0
          : (member?.effectiveRoles || []).length > 0;
      // alert anonymous user about invalid share key in URL
      if (shareKeyInUrl && !isEntityMember) {
        toasted({
          promise: Promise.reject(),
          errorMessage: `Invalid or expired share link. Please try a new share link or contact help@highnote.fm for further assistance.`,
        });
      }
      query.delete("shareKey");
    } else if (shareKeyInEntity !== shareKeyInUrl) {
      query.set("shareKey", shareKeyInEntity);
    }
    history.replace({
      pathname: location.pathname,
      search: query.toString(),
    });
  }, [entity, membersV2Enabled, resolveMember]);
};

/**
 * A local share-role edit that no `share` member document snapshot has
 * confirmed yet: an overlay, shown on top of the server value until the
 * server catches up.
 *
 * This replaces the `space.updatedAt` fence (the "keep local edits until a
 * newer server value arrives" check in the effect below) for the roles half
 * only, and only while the new read model is on. That fence assumes every
 * membership change bumps `space.updatedAt`. Under the flag the rendered
 * value comes from the share member document, and the dual-write trigger
 * writes that document after the space document. So a `space.updatedAt`
 * fence releases one snapshot early: the toggle flips back to its old
 * value, then forward. And when the space document no longer carries
 * membership at all, the fence never releases: the toggles freeze after the
 * first local edit, silently, and only for the person who made it. That is
 * the hardest possible shape to reproduce from a bug report.
 *
 * `at` is wall-clock time on the client, and `member.updatedAt` is
 * wall-clock time on the server. So a client with a skewed clock can hold
 * the overlay slightly too long, or drop it slightly early. The next
 * snapshot bounds both cases, and both are strictly better than a fence
 * that never releases. Do not "fix" the skew with a comparison of role
 * arrays: a write that sets the same roles back is a legitimate no-op, and
 * that comparison never clears the overlay.
 *
 * `roles: null` means the user disabled the link, which deletes the member
 * document. That edit resolves when the document is gone, not when it
 * updates. A wait for `updatedAt` on a document that no longer exists hangs
 * the overlay forever.
 */
type PendingShareEdit = { roles: ROLE[] | null; at: number };

interface EntityShareSettings {
  isShared: boolean;
  shareRoles: ROLE[];
  visibility: SpaceVisibility | undefined;
  sharingUpdatedAt: number;
}

export const InviteByLink = () => {
  const { trackEvent } = useSegmentContext();
  const { addErrorMessage } = useToast();
  const {
    isEntityOwner,
    entity,
    entityHasChildren,
    updateEntityRoles,
    updateEntityPassword,
    defaultFeatureAlertMessage,
    canManageEntity,
    canManagePassword,
    canManageDownloadControl,
  } = useShareDialogContext();
  const { trackId } = useUrlContext();
  const legacyEntityRoles = entity?.rolesV2 || {};

  /* The share-link read: under this flag, the link's roles come from the
   * `share` member document instead of `space.rolesV2`. */
  const { membersV2Enabled } = useMembershipFlags();
  const access = useSpaceAccessMembers({
    spaceId: entity?.id,
    canManage: canManageEntity,
    enabled: membersV2Enabled,
  });
  /*
   * A denied or still-loading access listener falls back to the in-document
   * map. The migration's dual-write contract guarantees that map is still
   * correct while the legacy fields are still written. This property is
   * what makes the flag flip safe: the worst case is today's behavior.
   */
  const useAccessDocs =
    membersV2Enabled && canManageEntity && !access.loading && !access.error;

  // Get all the valid share keys (must have roles attached).
  const [legacyShareKey] = getEntityShareKeys(entity);

  /*
   * `memberId` is the document id, and the document id is the share key,
   * raw and not lower-cased. So this is the same string that keys the
   * legacy map.
   */
  const shareKey = useAccessDocs
    ? access.shareMember?.memberId
    : legacyShareKey;
  const serverShareRoles = useAccessDocs
    ? access.shareMember?.effectiveRoles || []
    : legacyEntityRoles[legacyShareKey] || [];
  const isShared = serverShareRoles.includes(ROLE.VIEW);

  // Share link. Contains trackId if we're on a Track page.
  const rootUrl = `${window.location.origin}/space`;
  let shareLink = `${rootUrl}/${entity?.id}`;
  if (trackId) shareLink += `/${trackId}`;
  if (shareKey) {
    shareLink += `?shareKey=${shareKey}`;
  }

  const [entityShareSettings, setEntityShareSettings] =
    useState<EntityShareSettings>({
      isShared,
      shareRoles: serverShareRoles,
      visibility: entity?.visibility,
      sharingUpdatedAt: entity?.updatedAt || 0,
    });

  /*
   * This effect is unchanged from before the share-link read, deliberately.
   * `visibility` is not a membership field: it lives on the space document,
   * `setSpaceVisibility` writes the space document, and so `space.updatedAt`
   * remains its correct fence under every flag. Only the roles half moves to
   * a different document, and only that half gets the overlay below.
   */
  useEffect(() => {
    if (!entity) {
      return;
    }

    setEntityShareSettings((prev) => {
      // local updates will take precedence over server updates
      // unless the server updates are newer
      if (entity?.updatedAt <= prev.sharingUpdatedAt) {
        return prev;
      }

      return {
        ...prev,
        isShared,
        shareRoles: isShared ? serverShareRoles : [],
        visibility: entity?.visibility,
        sharingUpdatedAt: entity?.updatedAt,
      };
    });
  }, [entity?.updatedAt, isShared, serverShareRoles]);

  const [pendingShareEdit, setPendingShareEdit] =
    useState<PendingShareEdit | null>(null);

  /*
   * Drop the overlay when the share member document reports a write at or
   * after the edit. For a disabled link, drop it when the document is gone.
   */
  useEffect(() => {
    if (!useAccessDocs || !pendingShareEdit) return;
    const member = access.shareMember;
    const resolved =
      pendingShareEdit.roles === null
        ? !member
        : !!member && (member.updatedAt || 0) >= pendingShareEdit.at;
    if (resolved) setPendingShareEdit(null);
  }, [useAccessDocs, access.shareMember, pendingShareEdit]);

  /*
   * The settings the toggles actually render from.
   *
   * With the flag off this is `entityShareSettings`, the same object, so
   * the legacy branch is byte-identical. With the flag on, the roles half
   * comes from the share member document with the overlay applied, and
   * `visibility` and `sharingUpdatedAt` still come from the space document.
   */
  const shareSettings = useMemo<EntityShareSettings>(() => {
    if (!useAccessDocs) return entityShareSettings;
    const roles = pendingShareEdit
      ? pendingShareEdit.roles || []
      : serverShareRoles;
    return {
      isShared: roles.includes(ROLE.VIEW),
      shareRoles: roles,
      visibility: entityShareSettings.visibility,
      sharingUpdatedAt: entityShareSettings.sharingUpdatedAt,
    };
  }, [useAccessDocs, entityShareSettings, pendingShareEdit, serverShareRoles]);

  const downloadToggleable = serverShareRoles.includes(ROLE.DOWNLOAD)
    ? canManageDownloadControl
    : canManageEntity;

  const isPrivateInboxEnabled = isSpacePrivateInboxEnabled(entity);
  const isRequireAccountDisabled =
    isPrivateInboxEnabled ||
    !shareSettings.isShared ||
    !entity?.subscriptionTier ||
    entity?.subscriptionTier === SUBSCRIPTION_TIER.FREE;

  const updateShareRoles = useMemo(() => {
    return debounce(async (roles: ROLE[] | null, onError: () => void) => {
      // If we don't have a share key, generate a new one.
      const [shareKey, ...staleKeys] = getEntityShareKeys(entity);
      const latestShareKey = shareKey || `${SHARE_KEY_PREFIX}${uuidv4()}`;

      // Clean up any old share keys that may have lingered.
      const updateData = { [latestShareKey]: roles };
      staleKeys.forEach((key) => (updateData[key] = null));
      try {
        await updateEntityRoles(updateData, true);
      } catch (e) {
        onError();
      }
    }, 500);
  }, [entity]);

  const isFreeTier = entity?.subscriptionTier === SUBSCRIPTION_TIER.FREE;

  const isDiscoverable = entityShareSettings.visibility === "visible";
  const discoverableDisabled = !canManageEntity || !shareSettings.isShared;

  const toggleDiscoverable = useCallback(async () => {
    if (!entity?.id) return;
    const previousSettings = entityShareSettings;
    const next: SpaceVisibility = isDiscoverable ? "unlisted" : "visible";
    setEntityShareSettings((prev) => ({
      ...prev,
      visibility: next,
      sharingUpdatedAt: Date.now(),
    }));
    try {
      await highnote.setSpaceVisibility({
        spaceId: entity.id,
        visibility: next,
      });
    } catch (e) {
      console.error("setSpaceVisibility failed", e);
      setEntityShareSettings(previousSettings);
      addErrorMessage("Could not update discoverability. Please try again.");
    }
  }, [entity?.id, entityShareSettings, isDiscoverable, addErrorMessage]);

  const toggleRole = useCallback(
    async (role: ROLE) => {
      if (role === ROLE.VIEW) return; // Direct toggling of ROLE.VIEW and ROLE.DOWNLOAD when privateInboxEnabled is true is skipped Direct toggling of ROLE.VIEW is skipped

      const newRolesSet = new Set(shareSettings.shareRoles);

      if (newRolesSet.has(role)) {
        newRolesSet.delete(role);
      } else {
        newRolesSet.add(role);
        // If enabling any role and ROLE.VIEW is not already enabled, enable it
        if (!newRolesSet.has(ROLE.VIEW)) {
          newRolesSet.add(ROLE.VIEW);
        }

        // If enabling any role and ROLE.DOWNLOAD is not already enabled and is a free tier user, enable it
        if (!newRolesSet.has(ROLE.DOWNLOAD) && isFreeTier) {
          newRolesSet.add(ROLE.DOWNLOAD);
        }
      }

      const updatedRoles = Array.from(newRolesSet);

      const previousSettings = entityShareSettings;
      const previousPending = pendingShareEdit;
      setEntityShareSettings((prev) => ({
        ...prev,
        isShared: updatedRoles.length > 0,
        shareRoles: updatedRoles,
        sharingUpdatedAt: Date.now(),
      }));
      setPendingShareEdit({ roles: updatedRoles, at: Date.now() });
      await updateShareRoles(updatedRoles, () => {
        // error handling is done inside `updateEntityRoles`
        // this is just to revert in case of failure
        setEntityShareSettings(previousSettings);
        setPendingShareEdit(previousPending);
      });
    },
    [
      updateShareRoles,
      entityShareSettings,
      shareSettings,
      pendingShareEdit,
      isPrivateInboxEnabled,
    ],
  );

  const handleEnableLinkToggle = useCallback(async () => {
    const previousSettings = entityShareSettings;
    const previousPending = pendingShareEdit;
    /*
     * With the flag off, `shareSettings` is `entityShareSettings`, so this
     * reads the same value that `!prev.isShared` read before this change.
     * With the flag on, it is the member document's value with the overlay
     * applied. That is the value the user sees, so it is the value the
     * toggle must invert.
     */
    const nextRoles = shareSettings.isShared
      ? null
      : [
          ROLE.VIEW,
          ROLE.COMMENT,
          ROLE.REPLY,
          ROLE.REACT,
          ROLE.UPLOAD,
          ROLE.DOWNLOAD,
          ROLE.GUESTS_ALLOWED,
        ];
    setEntityShareSettings((prev) => ({
      ...prev,
      isShared: !shareSettings.isShared,
      shareRoles: nextRoles || [],
      sharingUpdatedAt: Date.now(),
    }));
    setPendingShareEdit({ roles: nextRoles, at: Date.now() });
    await updateShareRoles(nextRoles, () => {
      // error handling is done inside `updateEntityRoles`
      // this is just to revert in case of failure
      setEntityShareSettings(previousSettings);
      setPendingShareEdit(previousPending);
    });
    if (!shareSettings.isShared) {
      trackEvent(
        EVENT_ID.CREATED_INVITE_LINK,
        {
          entityName: entity?.name,
          entityId: entity?.id,
          entityUrl: shareLink,
        },
        entity?.createdBy,
      );
    }
  }, [
    updateShareRoles,
    trackEvent,
    entity?.name,
    entity?.id,
    shareLink,
    entityShareSettings,
    shareSettings,
    pendingShareEdit,
  ]);

  return (
    <ErrorBoundary name="InviteByLink">
      <div className="ShareDialogLink InviteByLink">
        <List>
          <ListItemButton
            disabled={!canManageEntity}
            className="ShareDialogLink-main-toggle"
            onClick={handleEnableLinkToggle}
          >
            <div className="info">
              <h4>Enable Link</h4>
              <p>
                Invite anyone to collaborate in your Space without an account
              </p>
            </div>

            <PermissionTooltip hasPermission={canManageEntity}>
              <div>
                <Switch checked={shareSettings.isShared} />
              </div>
            </PermissionTooltip>
          </ListItemButton>
          <div
            className={classNames("ShareDialogLink-share-link", {
              isNotShared: !shareSettings.isShared,
            })}
          >
            <TextInput value={shareLink} />
            <CopyButton
              disabled={!shareSettings.isShared || !shareKey}
              theme={BUTTON_THEME.CTA}
              value={shareLink}
              onClick={() => {
                trackEvent(
                  EVENT_ID.COPIED_INVITE_LINK,
                  {
                    entityName: entity?.name,
                    entityId: entity?.id,
                    entityUrl: shareLink,
                  },
                  entity?.createdBy,
                );
              }}
            >
              <LinkSVG /> <span>Copy Link</span>
            </CopyButton>
          </div>

          <div className="link-options-header">
            <h4>Options</h4>
            <p>Control link permissions</p>
          </div>
          <ListItemButton
            disabled={!canManageEntity}
            className="ShareDialogLink-option"
            onClick={() => toggleRole(ROLE.COMMENT)}
          >
            <ChatSVG />
            <p>
              <span className="title">Allow Comments</span>
            </p>
            <PermissionTooltip hasPermission={canManageEntity}>
              <div>
                <Switch
                  checked={shareSettings.shareRoles.includes(ROLE.COMMENT)}
                />
              </div>
            </PermissionTooltip>
          </ListItemButton>
          <ListItemButton
            disabled={!canManageEntity}
            className="ShareDialogLink-option"
            onClick={() => toggleRole(ROLE.REPLY)}
          >
            <ChatFilledSVG />
            <p>
              <span className="title">Allow Replies</span>
            </p>
            <PermissionTooltip hasPermission={canManageEntity}>
              <div>
                <Switch
                  checked={shareSettings.shareRoles.includes(ROLE.REPLY)}
                />
              </div>
            </PermissionTooltip>
          </ListItemButton>
          {/* TODO: Uncomment when reactions are supported in the web app
          <ListItemButton
            disabled={!canManageEntity}
            className="ShareDialogLink-option"
            onClick={() => toggleRole(ROLE.REACT)}
          >
            <HeartSVG />
            <p>
              <span className="title">Allow Reactions</span>
            </p>
            <PermissionTooltip hasPermission={canManageEntity}>
              <div>
                <Switch
                  checked={shareSettings.shareRoles.includes(ROLE.REACT)}
                />
              </div>
            </PermissionTooltip>
          </ListItemButton>
          */}
          <ListItemButton
            disabled={!canManageEntity}
            className="ShareDialogLink-option"
            onClick={() => toggleRole(ROLE.UPLOAD)}
          >
            <UploadSVG />
            <p>
              <span className="title">Allow Uploads</span>
            </p>
            <PermissionTooltip hasPermission={canManageEntity}>
              <div>
                <Switch
                  checked={shareSettings.shareRoles.includes(ROLE.UPLOAD)}
                />
              </div>
            </PermissionTooltip>
          </ListItemButton>
          {!isPrivateInboxEnabled && (
            <ListItemButton
              disabled={
                isFreeTier || isPrivateInboxEnabled || !downloadToggleable
              }
              className="ShareDialogLink-option"
              onClick={() => toggleRole(ROLE.DOWNLOAD)}
            >
              <DownloadSVG />
              <p>
                <span className="title">Allow Downloads</span>
              </p>
              <PermissionTooltip
                title={
                  isEntityOwner
                    ? ControlledFeatureTooltipLabel[
                        CONTROLLED_SHARE_FEATURES.DOWNLOAD
                      ]
                    : defaultFeatureAlertMessage
                }
                hasPermission={
                  canManageEntity && downloadToggleable && !isFreeTier
                }
              >
                <div>
                  <Switch
                    checked={
                      shareSettings.shareRoles.includes(ROLE.DOWNLOAD) ||
                      isFreeTier
                    }
                  />
                </div>
              </PermissionTooltip>
            </ListItemButton>
          )}
          <ListItemButton
            disabled={discoverableDisabled}
            className="ShareDialogLink-option"
            onClick={toggleDiscoverable}
          >
            <EyeSVG />
            <p>
              <span className="title">Make Discoverable</span>
            </p>
            <PermissionTooltip
              hasPermission={!discoverableDisabled}
              title={
                !canManageEntity
                  ? undefined
                  : "Enable the share link to make this space discoverable."
              }
            >
              <div>
                <Switch checked={isDiscoverable} />
              </div>
            </PermissionTooltip>
          </ListItemButton>
          {canManageEntity && (
            <React.Fragment>
              <div className="link-options-header">
                <h4>Additional Security</h4>
                <p>Control link security</p>
              </div>
              <EntityPasswordEditor
                isEntityShared={isShared}
                entity={entity as Entity}
                entityHasChildren={entityHasChildren}
                canManagePassword={canManagePassword}
                password={entity?.sharePassword}
                passwordEnabled={Boolean(entity?.sharePasswordEnabled)}
                updatePassword={(props) =>
                  updateEntityPassword({
                    passwordType: PASSWORD_TYPE.Share,
                    ...props,
                  })
                }
                className="ShareDialogLink-option"
              />
              {!isPrivateInboxEnabled && (
                <ListItemButton
                  disabled={isRequireAccountDisabled}
                  className="ShareDialogLink-option"
                  onClick={() => toggleRole(ROLE.GUESTS_ALLOWED)}
                >
                  <UserSVG />
                  <p>
                    <span className="title">Require Account</span>
                  </p>
                  <PermissionTooltip
                    hasPermission={!isRequireAccountDisabled}
                    title={
                      isShared
                        ? "Upgrade to unlock this feature."
                        : "Sharing is disabled for this resource."
                    }
                  >
                    <div>
                      <Switch
                        checked={
                          // Free tier users can't control this and should always be off
                          !isRequireAccountDisabled &&
                          !shareSettings.shareRoles.includes(
                            ROLE.GUESTS_ALLOWED,
                          )
                        }
                      />
                    </div>
                  </PermissionTooltip>
                </ListItemButton>
              )}
            </React.Fragment>
          )}
        </List>
      </div>
    </ErrorBoundary>
  );
};
